import { WS_EVENT } from "@the-drop/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { playOrderReceivedChime } from "../lib/play-order-received-sound";
import { useDispensarySocketInstance } from "../workspace/dispensary-socket-context";

/**
 * Invalidates dashboard queries and triggers UX side effects (chime, sound)
 * when the dispensary socket emits canonical events (see `WS_EVENT` in
 * `@the-drop/types`).
 *
 * **Note on `driver.location`:** this hook no longer handles that event. Live
 * driver positions are owned by `useDispensaryDriversLive`, which mirrors
 * `transporter-web`'s `useTransporterDriversLive` so both apps share one
 * pipeline. Pre-this-change, the handler here mutated `lastLat`/`lastLng` on
 * the `DashboardActiveTransporter[]` cache — but the API never populated
 * those fields with real coordinates (they were hardcoded `39.73 + index`
 * Denver dots), so the mutation only ever overrode fake data with real data
 * for the brief window between page load and the first ping. Removed
 * entirely; positions now flow through the dedicated hook.
 */
export function useDispensaryDashboardLive(dispensaryId: string | null) {
  const queryClient = useQueryClient();
  const socket = useDispensarySocketInstance();
  const enabled = Boolean(dispensaryId);

  useEffect(() => {
    if (!enabled || !socket || !dispensaryId) return;

    const onOrderReceived = () => {
      void playOrderReceivedChime();
      void queryClient.invalidateQueries({ queryKey: ["dispensary", "metrics", dispensaryId] });
      void queryClient.invalidateQueries({ queryKey: ["dispensary", "orders", "recent", dispensaryId] });
      void new Audio("/sounds/order-notification.mp3").play().catch(() => {});
    };

    const onOrderConfirmed = () => {
      void queryClient.invalidateQueries({ queryKey: ["dispensary", "orders", "recent", dispensaryId] });
      void queryClient.invalidateQueries({ queryKey: ["dispensary", "active-runs", dispensaryId] });
    };

    socket.on(WS_EVENT.ORDER_RECEIVED, onOrderReceived);
    socket.on("order.confirmed", onOrderConfirmed);

    return () => {
      socket.off(WS_EVENT.ORDER_RECEIVED, onOrderReceived);
      socket.off("order.confirmed", onOrderConfirmed);
    };
  }, [enabled, queryClient, socket, dispensaryId]);
}
